import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight, Award, Shield, Target, Users, Brain, CheckCircle, Globe, Building2 } from 'lucide-react';

// Partner logo component - uses image if available, falls back to styled badge
const PartnerLogo = ({ logo, logoText, brand }) => {
  if (logo) {
    // Larger logos for specific partners
    const isLargeLogo = brand === 'gallup' || brand === 'lcp';
    return (
      <img 
        src={logo} 
        alt={`${logoText} logo`}
        className={`${isLargeLogo ? 'h-16 lg:h-20 max-w-[220px]' : 'h-12 lg:h-14 max-w-[160px]'} w-auto object-contain`}
      />
    );
  }
  
  // Fallback to styled badge for partners without uploaded logos
  const brandStyles = {
    gallup: { bg: "#00205b", text: "#ffffff", accent: "#00b388" }
  };
  const style = brandStyles[brand] || { bg: "#1a365d", text: "#ffffff", accent: "#3182ce" };
  
  return (
    <div 
      className="inline-flex items-center px-4 py-2 rounded-lg font-bold text-sm tracking-wide shadow-md"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      <span className="mr-1" style={{ color: style.accent }}>●</span>
      {logoText}
    </div>
  );
};

const partnerTools = [
  {
    name: "Hogan Assessments",
    brand: "hogan",
    logoText: "HOGAN",
    logo: "/opt/brand/hogan.webp",
    description: "Hogan is the global leader in personality assessment for workplace performance. Hogan tools measure normal personality, derailment risks, and core values, providing insight into how leaders will perform, what risks they may pose, and what drives them.",
    products: [
      "Hogan Personality Inventory (HPI): Bright-side personality",
      "Hogan Development Survey (HDS): Derailment risks under pressure",
      "Motives, Values, Preferences Inventory (MVPI): Core drivers and fit"
    ],
    useCases: [
      "Leadership selection and succession",
      "Executive coaching",
      "High-potential identification",
      "Team composition and risk"
    ],
    partnerNote: "RESILIGENT is a certified Hogan partner, qualified to administer, interpret, and debrief Hogan assessments across Africa."
  },
  {
    name: "Leadership Circle Profile",
    brand: "lcp",
    logoText: "LEADERSHIP CIRCLE",
    logo: "/opt/brand/lcp-logo-forest.webp",
    description: "The Leadership Circle Profile (LCP) is a 360-degree assessment that measures both leadership competencies and the underlying assumptions that drive behaviour. It provides a powerful view of leadership effectiveness and the internal operating system that shapes it.",
    products: [
      "Leadership Circle Profile™: Individual leadership 360",
      "Leadership Culture Survey™: Organisational leadership culture assessment",
      "Manager Edition: For frontline and middle managers"
    ],
    useCases: [
      "Executive development and coaching",
      "Leadership team effectiveness",
      "Culture transformation",
      "Succession and readiness assessment"
    ],
    partnerNote: "RESILIGENT is a certified Leadership Circle practitioner, qualified to deploy LCP assessments and facilitate leadership development based on results."
  },
  {
    name: "Everything DiSC®",
    brand: "disc",
    logoText: "Everything DiSC®",
    logo: "/opt/brand/130-1306355-logo-everything-disc-ap-everything-disc-workplace-logo-png.webp",
    description: "Everything DiSC® is Wiley's flagship behavioural assessment platform, offering a range of applications for workplace communication, management, leadership, and sales. It provides accessible, actionable insight into behavioural styles and interpersonal dynamics.",
    products: [
      "Everything DiSC Workplace®: Foundation behavioural insight",
      "Everything DiSC® Management: For people managers",
      "Everything DiSC Work of Leaders®: Vision, alignment, execution",
      "Everything DiSC® Agile EQ™: Emotional intelligence development"
    ],
    useCases: [
      "Team development and collaboration",
      "Management and leadership programmes",
      "Onboarding and communication training",
      "Conflict resolution"
    ],
    partnerNote: "RESILIGENT is an authorised Everything DiSC partner across Africa, able to deploy the full suite of DiSC applications."
  },
  {
    name: "CliftonStrengths",
    brand: "gallup",
    logoText: "CLIFTONSTRENGTHS",
    logo: "/opt/brand/1694992438808.webp",
    description: "CliftonStrengths (formerly StrengthsFinder) is Gallup's strengths-based development tool that identifies an individual's top talent themes. It shifts the development conversation from fixing weaknesses to maximising natural strengths.",
    products: [
      "CliftonStrengths 34: Full strengths profile",
      "CliftonStrengths for Managers: Team strengths insight",
      "CliftonStrengths for Students: Early career development"
    ],
    useCases: [
      "Individual development planning",
      "Team composition and collaboration",
      "Manager effectiveness",
      "Engagement and retention strategies"
    ],
    partnerNote: "RESILIGENT is a Gallup-Certified Strengths Coach, qualified to facilitate strengths-based development programmes."
  }
];

const PartnerAssessmentsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Hogan Assessment Nigeria | Gallup StrengthsFinder Lagos | RESILIGENT"
        description="Certified Hogan, Gallup CliftonStrengths, Leadership Circle Profile, and Everything DISC assessments in Nigeria. Qualified partner for psychometric assessments across Lagos and Africa. Expert interpretation for Nigerian leadership contexts."
        keywords="Hogan assessment Nigeria, Gallup StrengthsFinder Lagos, Leadership Circle Profile Nigeria, Everything DISC Lagos, psychometric assessment Nigeria, Hogan certified Nigeria, CliftonStrengths Africa, personality assessment Lagos"
        canonical="/partner-assessments"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/ext/u1573164574397-dd250bc8a598.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-px bg-[#2693d0]" />
                <p className="text-[#2693d0] text-sm font-medium tracking-wide uppercase">
                  Global Tools, Local Expertise
                </p>
              </div>
              
              <h1 className="text-white font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
                Partner Assessments
              </h1>
              
              <h2 className="text-[#2693d0] text-lg lg:text-xl xl:text-2xl font-medium leading-relaxed mb-8">
                World-Class Diagnostic Tools, Delivered with African Expertise
              </h2>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                RESILIGENT is a regional partner and qualified to administer these respected diagnostic tools across Africa, bringing global assessment rigour with local interpretation and contextual relevance.
              </p>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Why Partner Assessments Matter
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  While RESILIGENT has developed proprietary diagnostics designed specifically for African contexts, we recognise that some organisations require or prefer globally recognised assessment tools.
                </p>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  As certified partners of leading assessment providers, we bring the same rigour and interpretive depth to these tools as we do to our own diagnostics.
                </p>
              </div>
              
              <div className="bg-[#f0f5f8] rounded-xl p-8 border border-slate-100">
                <p className="text-slate-700 font-medium text-lg mb-6">What we offer:</p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">Certified administration and quality assurance</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">Expert interpretation with African contextual insight</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">Integration with coaching and development programmes</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">Local support and ongoing advisory</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Partner Tools - Individual Sections */}
        {partnerTools.map((tool, index) => (
          <section 
            key={tool.name}
            className={`py-20 lg:py-28 ${index % 2 === 0 ? 'bg-[#f0f5f8]' : 'bg-white'}`}
          >
            <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                        <Award className="w-5 h-5 text-[#2693d0]" />
                      </div>
                      <span className="text-[#2693d0] text-sm font-semibold tracking-wide uppercase">Partner Tool</span>
                    </div>
                    <PartnerLogo logo={tool.logo} logoText={tool.logoText} brand={tool.brand} />
                  </div>
                  
                  <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                    {tool.name}
                  </h2>
                  
                  <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-8">
                    {tool.description}
                  </p>
                  
                  <div className="bg-[#2693d0]/5 rounded-xl p-5 border border-[#2693d0]/20">
                    <p className="text-slate-700 text-sm lg:text-base leading-relaxed">
                      <span className="font-semibold text-[#2693d0]">RESILIGENT Partnership:</span> {tool.partnerNote}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  {/* Products */}
                  <div className={`rounded-xl p-6 border ${index % 2 === 0 ? 'bg-white border-slate-200' : 'bg-[#f0f5f8] border-slate-100'}`}>
                    <h3 className="text-slate-800 font-semibold text-lg mb-4">Available assessments:</h3>
                    <ul className="space-y-3">
                      {tool.products.map((product, pIndex) => (
                        <li key={pIndex} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                          <span className="text-slate-700 text-base leading-relaxed">{product}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Use Cases */}
                  <div className={`rounded-xl p-6 border ${index % 2 === 0 ? 'bg-white border-slate-200' : 'bg-[#f0f5f8] border-slate-100'}`}>
                    <h3 className="text-slate-800 font-semibold text-lg mb-4">Common use cases:</h3>
                    <ul className="space-y-3">
                      {tool.useCases.map((useCase, uIndex) => (
                        <li key={uIndex} className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-md bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-3.5 h-3.5 text-[#2693d0]" />
                          </div>
                          <span className="text-slate-700 text-base leading-relaxed">{useCase}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Integration Note */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                  Integrated with Our Advisory Approach
                </h2>
                <p className="text-slate-200 text-base lg:text-lg leading-relaxed mb-6">
                  Partner assessments are not deployed in isolation. They are integrated with RESILIGENT's advisory-led approach, ensuring that insight leads to action.
                </p>
                <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                  Whether used alongside our proprietary diagnostics or as standalone tools, partner assessments benefit from our interpretive expertise and connection to coaching, development, and organisational change.
                </p>
              </div>
              
              <div className="bg-white/10 rounded-xl p-8 border border-white/20">
                <p className="text-slate-200 font-medium text-lg mb-6">How we add value:</p>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">African contextual interpretation</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Expert debrief and feedback sessions</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Connection to development and coaching</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Organisational-level insight aggregation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Looking for Assessment Support in Africa?
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-10">
                Whether you need Hogan, Leadership Circle Profile, Everything DISC, or Gallup CliftonStrengths, RESILIGENT can help you deploy these tools with local expertise and interpretive depth.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#2693d0] text-white px-8 py-4 rounded-lg font-medium hover:bg-[#1e7ab8] transition-all duration-300"
                >
                  Discuss your assessment needs
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="/diagnostics"
                  className="inline-flex items-center justify-center gap-2 bg-white text-slate-800 px-8 py-4 rounded-lg font-medium border border-slate-200 hover:border-[#2693d0] hover:text-[#2693d0] transition-all duration-300"
                >
                  Explore RESILIGENT diagnostics
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default PartnerAssessmentsPage;
